import { FC } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import { FcGoogle } from 'react-icons/fc';
import { Link } from 'react-router-dom';
import { Checkbox, Input } from 'ui/components';
import { Button } from 'ui/components/common';
import { SIZE } from 'utils/constants';
import * as Yup from 'yup';
import { cn } from 'utils/helpers';
import { PhoneNumber } from 'ui/components/form';
import { debounce } from 'lodash';
import { AppleSignInButton } from 'pages/public/components/common';

import { REGISTER_FORM } from '../Register';

const FORM_INIT: REGISTER_FORM = {
  emailAddress: '',
  mobileNumber: '',
  password: '',
  passwordConfirmation: '',
  firstName: '',
  lastName: '',
  termsConditions: false,
};

const REGISTRATION_SCHEMA = Yup.object().shape({
  emailAddress: Yup.string().email().required('Required'),
  mobileNumber: Yup.string().required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Required'),
  passwordConfirmation: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords do not match')
    .required('Required'),
  termsConditions: Yup.boolean().required('Required'),
});

interface PROPS {
  onSubmitForm: (
    payload: REGISTER_FORM,
    actions: FormikHelpers<REGISTER_FORM>,
  ) => void;
  isLoading: boolean;
}

const RegisterForm: FC<PROPS> = ({ onSubmitForm, isLoading }) => {
  const handleGoogleLogin = () => {
    window.location.href = `${import.meta.env.VITE_APP_END_POINT}/api/v1/auth/accounts/google`;
  };

  return (
    <Formik
      initialValues={FORM_INIT}
      onSubmit={onSubmitForm}
      validationSchema={REGISTRATION_SCHEMA}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setErrors,
        handleBlur,
        isSubmitting,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="text-left">
          <Button
            onClick={handleGoogleLogin}
            label="CONTINUE WITH GOOGLE"
            className="w-full mb-2 font-semibold"
            intent="tertiary"
            size="md"
            icon={FcGoogle}
            left
            disabled={isLoading}
          />
          <AppleSignInButton isLoading={isLoading} />
          <p className="text-sm text-center leading-[22px] my-5">
            or use your email to sign up
          </p>
          <div className="grid grid-cols-2 gap-3 mb-7">
            <Input
              className="col-span-2"
              type="text"
              name="firstName"
              size={SIZE.Large}
              textType={
                errors.firstName && touched.firstName ? 'danger' : 'default'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
              placeholder="First Name"
              subText={
                errors.firstName && touched.firstName ? errors.firstName : ''
              }
              required
              disabled={isSubmitting}
            />
            <Input
              className="col-span-2"
              type="text"
              name="lastName"
              size={SIZE.Large}
              textType={
                errors.lastName && touched.lastName ? 'danger' : 'default'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              placeholder="Last Name"
              subText={
                errors.lastName && touched.lastName ? errors.lastName : ''
              }
              required
              disabled={isSubmitting}
            />
            <Input
              className="col-span-2"
              type="text"
              name="emailAddress"
              size={SIZE.Large}
              textType={
                errors.emailAddress && touched.emailAddress
                  ? 'danger'
                  : 'default'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.emailAddress}
              placeholder="Work Email"
              subText={
                errors.emailAddress && touched.emailAddress
                  ? errors.emailAddress
                  : ''
              }
              required
              disabled={isSubmitting}
            />
            <PhoneNumber
              className="col-span-2"
              name="mobileNumber"
              size={SIZE.Large}
              textType={
                errors.mobileNumber && touched.mobileNumber
                  ? 'danger'
                  : 'default'
              }
              onChange={debounce(setFieldValue, 1000)}
              value={values.mobileNumber}
              placeholder="Phone Number"
              subText={
                errors.mobileNumber && touched.mobileNumber
                  ? errors.mobileNumber
                  : ''
              }
              onBlur={(name, val) =>
                handleBlur({ target: { name, value: val } })
              }
              onError={(e, message) => setErrors({ ...errors, [e]: message })}
              disabled={isSubmitting}
            />
            <Input
              className="col-span-2 md:col-span-1"
              type="password"
              name="password"
              size={SIZE.Large}
              textType={
                errors.password && touched.password ? 'danger' : 'default'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              placeholder="Password"
              subText={
                errors.password && touched.password ? errors.password : ''
              }
              required
              disabled={isSubmitting}
            />
            <Input
              className="col-span-2 md:col-span-1"
              type="password"
              name="passwordConfirmation"
              size={SIZE.Large}
              textType={
                errors.passwordConfirmation && touched.passwordConfirmation
                  ? 'danger'
                  : 'default'
              }
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.passwordConfirmation}
              placeholder="Re-enter password"
              subText={
                errors.passwordConfirmation && touched.passwordConfirmation
                  ? errors.passwordConfirmation
                  : ''
              }
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="flex items-center gap-x-2">
            <div className="flex gap-2.5">
              <Checkbox
                checked={values.termsConditions}
                onChange={(e) =>
                  setFieldValue('termsConditions', e.target.checked)
                }
              />
              <p className="text-xs">
                By signing up, you agree to our
                <Link to="/" className="ml-1 font-semibold text-primary-base">
                  Terms & Conditions
                </Link>
              </p>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full my-5"
            disabled={
              isLoading ||
              !!Object.keys(errors).length ||
              !Object.keys(touched).length ||
              !values.termsConditions
            }
            label="TRY FREE"
            size="lg"
          />
          <p className={cn('text-center', 'text-sm')}>
            Already have an account?
            <Link to="/login" className="ml-1 text-primary-base">
              Login
            </Link>
          </p>
        </form>
      )}
    </Formik>
  );
};

export default RegisterForm;
